import { Action } from 'redux'
import { ThunkAction } from 'redux-thunk'
import {
  aggregateByTokeys,
  aggregateByCsv,
  confirmIncentiveList,
  AggregateResponse,
  IncentiveEntry,
} from '../api/incentiveListApi'

// 集約結果の各行に画面側のチェック状態を持たせる
export type CheckedEntry = IncentiveEntry & { checked: boolean }

// モーダル開く
export function openIncentiveListModal(): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_OPEN_MODAL' }
}

// モーダル閉じる（state初期化）
export function closeIncentiveListModal(): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_CLOSE_MODAL' }
}

// 集約結果セット
function setAggregateResult(payload: AggregateResponse): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_SET_RESULT', payload }
}

// ローディング状態
function setLoading(loading: boolean): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_SET_LOADING', loading }
}

// エラー状態
function setError(error: string | null): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_SET_ERROR', error }
}

// 1行のチェックをトグル
export function toggleIncentiveEntry(index: number): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_TOGGLE_ENTRY', index }
}

// 全行のチェックを一括設定
export function setAllIncentiveChecked(checked: boolean): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_SET_ALL_CHECKED', checked }
}

// CV一覧の絞り込みを解除
export function clearMatchedCvFilter(): IncentiveListAction {
  return { type: 'INCENTIVE_LIST_CLEAR_MATCHED' }
}

// tokey群を投げて集約APIを呼ぶ
export function requestAggregateByTokeys(
  clientId: number,
  tokeys: string[],
): ThunkAction<Promise<void>, null, null, IncentiveListAction> {
  return async (dispatch) => {
    dispatch(setLoading(true))
    dispatch(setError(null))
    try {
      const result = await aggregateByTokeys(clientId, tokeys)
      dispatch(setAggregateResult(result))
    } catch (e: any) {
      dispatch(setError(e?.response?.data?.error || e?.message || '集約に失敗しました'))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

// CSVを投げて集約APIを呼ぶ
export function requestAggregateByCsv(
  clientId: number,
  file: File,
): ThunkAction<Promise<void>, null, null, IncentiveListAction> {
  return async (dispatch) => {
    dispatch(setLoading(true))
    dispatch(setError(null))
    try {
      const result = await aggregateByCsv(clientId, file)
      dispatch(setAggregateResult(result))
      // 成功時はモーダルを閉じてCV一覧側で結果を確認できるようにする
      dispatch(closeIncentiveListModal())
    } catch (e: any) {
      dispatch(setError(e?.response?.data?.error || e?.message || 'CSV処理に失敗しました'))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

// チェック済みtokeyを発行確定し、残りのmatchedTokeysで再集約して一覧を更新する。
// 発行済みは backend の aggregate 側で除外されるため、確定分は一覧から消える。
export function requestConfirmIncentive(
  clientId: number,
  tokeysToConfirm: string[],
  matchedTokeys: string[],
  csvFriendEmails: Record<string, string>,
): ThunkAction<Promise<void>, null, null, IncentiveListAction> {
  return async (dispatch) => {
    dispatch(setLoading(true))
    dispatch(setError(null))
    try {
      await confirmIncentiveList(clientId, tokeysToConfirm)
      const result = await aggregateByTokeys(clientId, matchedTokeys, csvFriendEmails)
      dispatch(setAggregateResult(result))
    } catch (e: any) {
      dispatch(setError(e?.response?.data?.error || e?.message || '発行確定に失敗しました'))
    } finally {
      dispatch(setLoading(false))
    }
  }
}

interface OpenModal extends Action {
  type: 'INCENTIVE_LIST_OPEN_MODAL'
}
interface CloseModal extends Action {
  type: 'INCENTIVE_LIST_CLOSE_MODAL'
}
interface SetResult extends Action {
  type: 'INCENTIVE_LIST_SET_RESULT'
  payload: AggregateResponse
}
interface SetLoading extends Action {
  type: 'INCENTIVE_LIST_SET_LOADING'
  loading: boolean
}
interface SetError extends Action {
  type: 'INCENTIVE_LIST_SET_ERROR'
  error: string | null
}
interface ToggleEntry extends Action {
  type: 'INCENTIVE_LIST_TOGGLE_ENTRY'
  index: number
}
interface SetAllChecked extends Action {
  type: 'INCENTIVE_LIST_SET_ALL_CHECKED'
  checked: boolean
}
interface ClearMatched extends Action {
  type: 'INCENTIVE_LIST_CLEAR_MATCHED'
}

export type IncentiveListAction =
  | OpenModal
  | CloseModal
  | SetResult
  | SetLoading
  | SetError
  | ToggleEntry
  | SetAllChecked
  | ClearMatched
